import {
  ConditionType,
  getInitialTraitColumn,
  isRelatedColumn,
  isRelatedJourneyEventColumn,
  isTraitColumn,
  type AndCondition,
  type OrCondition,
  type PropertyCondition,
  type RelatedColumn,
  type RootCondition,
  type TraitColumn,
} from "src/types/visual";
import { getTraitPropertyTypeFromTraitDefinition } from "src/components/explore/visual/utils";

/**
 * A realtime node stores only the compiled `realtime_conditions` that the Go
 * loader reads. The drawer still edits in the query builder's own shape, so a
 * saved node is mapped back on open — the inverse of
 * `compileSegmentConditions` in @hightouch/lib/journeys/realtime-conditions.
 *
 * Only called when the authoring field is absent, so a node saved before
 * compiled-only storage keeps rendering from what it already has.
 */

export type TraitForDecompile = Parameters<typeof getInitialTraitColumn>[0] & {
  id: string;
};

type DecompileContext = {
  traitsById: ReadonlyMap<string, TraitForDecompile>;
};

type CompiledNode = {
  type?: string;
  conditions?: unknown[];
  segment_id?: number | string;
  match?: string;
  trait_definition_id?: string;
  property?: PropertyCondition["property"];
  operator?: string;
  value?: unknown;
};

const isTraitProperty = (
  property: PropertyCondition["property"] | undefined | null,
): property is RelatedColumn & { column: TraitColumn } =>
  property != null &&
  isRelatedColumn(property) &&
  isTraitColumn(property.column);

const decompileLeaf = (
  leaf: CompiledNode,
  context: DecompileContext,
  dropped: string[],
): RootCondition | undefined => {
  if (leaf.type === "audience") {
    if (leaf.segment_id == null) {
      dropped.push("an audience condition with no audience");
      return undefined;
    }
    return {
      type: ConditionType.SegmentSet,
      modelId: String(leaf.segment_id),
      // `enter` and `in` both mean the profile is on the inside of the
      // audience; the entry node is the only place the delta form is written.
      includes: leaf.match !== "not_in" && leaf.match !== "exit",
    } as RootCondition;
  }

  // Warehouse leaves are stored in the query builder's own shape, so they need no mapping.
  if (
    leaf.type === ConditionType.Event ||
    leaf.type === ConditionType.NumberOf ||
    (leaf.type === ConditionType.Property && !isTraitProperty(leaf.property))
  ) {
    return leaf as RootCondition;
  }

  if (leaf.type === "trait" || leaf.type === ConditionType.Property) {
    const property = leaf.property ?? null;
    if (
      leaf.type === ConditionType.Property &&
      isRelatedJourneyEventColumn(property)
    ) {
      return leaf as PropertyCondition;
    }
    const traitDefinitionId =
      leaf.type === "trait"
        ? leaf.trait_definition_id
        : isTraitProperty(property)
          ? property.column.traitDefinitionId
          : undefined;
    const trait =
      traitDefinitionId != null
        ? context.traitsById.get(traitDefinitionId)
        : undefined;
    // A trait that isn't in the workspace's list can't be rebuilt into a
    // column. It is reported rather than quietly skipped: the caller writes the
    // rebuilt tree back over the stored one, so a silent skip is data loss.
    if (!trait) {
      dropped.push(`trait ${traitDefinitionId ?? "(unknown)"}`);
      return undefined;
    }
    if (leaf.type === ConditionType.Property) {
      return leaf as PropertyCondition;
    }
    return {
      type: ConditionType.Property,
      property: getInitialTraitColumn(trait),
      propertyType: getTraitPropertyTypeFromTraitDefinition(
        trait as Parameters<typeof getTraitPropertyTypeFromTraitDefinition>[0],
      ),
      operator: leaf.operator,
      value: leaf.value,
    } as unknown as PropertyCondition as RootCondition;
  }

  // `event_performed` is not a tree leaf in the authoring model: the event and
  // its filters live on the node's own columns and `event_conditions`.
  return undefined;
};

type DecompiledTree =
  | AndCondition<RootCondition>
  | OrCondition<RootCondition>
  | undefined;

const decompileTree = (
  compiled: unknown,
  context: DecompileContext,
  dropped: string[],
): DecompiledTree => {
  if (compiled == null || typeof compiled !== "object") {
    return undefined;
  }
  const node = compiled as CompiledNode;

  if (node.type === "and" || node.type === "or") {
    const conditions = (node.conditions ?? [])
      .map((child) => {
        const childNode = child as CompiledNode;
        if (childNode?.type === "and" || childNode?.type === "or") {
          return decompileTree(childNode, context, dropped);
        }
        return decompileLeaf(childNode, context, dropped);
      })
      .filter((condition): condition is RootCondition => condition != null);

    return {
      type: node.type === "and" ? ConditionType.And : ConditionType.Or,
      conditions,
    } as AndCondition<RootCondition> | OrCondition<RootCondition>;
  }

  // The loader accepts a bare leaf and wraps it too, so the renderer
  // normalises rather than leaving an unwrapped condition in the tree.
  const leaf = decompileLeaf(node, context, dropped);
  return leaf == null
    ? undefined
    : ({
        type: ConditionType.And,
        conditions: [leaf],
      } as AndCondition<RootCondition>);
};

export type DecompileResult = {
  condition: DecompiledTree;
  /** Leaves the rebuild could not represent. Non-empty means the tree is not
   * safe to write back over the stored one. */
  dropped: string[];
};

export const decompileRealtimeConditionsResult = (
  compiled: unknown,
  context: DecompileContext,
): DecompileResult => {
  const dropped: string[] = [];
  return { condition: decompileTree(compiled, context, dropped), dropped };
};

type CompiledEventPerformed = CompiledNode & { property_filters?: unknown };

// An event trigger compiles to the event leaf alone, or to an AND of the event leaf and the profile conditions.
const compiledEventTriggerParts = (
  compiled: unknown,
):
  | { event: CompiledEventPerformed; profileConditions: unknown }
  | undefined => {
  if (compiled == null || typeof compiled !== "object") {
    return undefined;
  }
  const node = compiled as CompiledNode;
  if (node.type === "event_performed") {
    return { event: node, profileConditions: undefined };
  }
  const [event, profileConditions] = node.conditions ?? [];
  if (
    node.type === "and" &&
    node.conditions?.length === 2 &&
    (event as CompiledNode | undefined)?.type === "event_performed"
  ) {
    return { event: event as CompiledEventPerformed, profileConditions };
  }
  return undefined;
};

/**
 * The property filters of a compiled `event_performed`, which are stored
 * verbatim in the query builder's own shape and need no mapping.
 */
export const decompileEventPerformedFilters = (
  compiled: unknown,
): unknown | undefined =>
  compiledEventTriggerParts(compiled)?.event.property_filters;

/** The compiled profile conditions beside an event trigger, which decompile like any other tree. */
export const compiledEventProfileConditions = (compiled: unknown): unknown =>
  compiledEventTriggerParts(compiled)?.profileConditions;
